import React, { useContext, useEffect, useMemo, useState } from "react";
import { EDSSearch } from "./EDSSearch";
import { DiaryContext } from "../../context/DiaryContext";
import { useAppContext } from "../../hooks/useAppContext";
import { INavigationMenuItem } from "../../types/NavigationMenu";
import { useUrlNavigation } from "../../hooks/useUrlSearchParams";
import { getVenueLink } from "@/utils/navigation/menuItems";
import { ToggleView } from "../Pages/Diary/SubHeader/SubHeader";

export type EDSVenuePickerProps = {
  parentItem: INavigationMenuItem;
  currentVenueId?: string;
  setExpandedItems?: (expandedItems: Set<string>) => void;
};

export const EDSVenuePicker: React.FC<EDSVenuePickerProps> = (props) => {
  const { parentItem, currentVenueId, setExpandedItems } = props;
  const { updateUrl } = useUrlNavigation();
  const { date, venues: allVenues } = useContext(DiaryContext);
  const { venueUserConfig } = useAppContext();

  const [searchCriteria, setSearchCriteria] = useState("");
  const [venueData, setVenueData] = useState<
    {
      label: string;
      value: any;
    }[]
  >([]);

  const venues = useMemo(() => {
    if (!allVenues || !venueUserConfig) {
      return [];
    }

    if (venueUserConfig.venues) {
      return allVenues.filter((venue: any) => !!venue.title);
    }

    if (Array.isArray(venueUserConfig.venues)) {
      return allVenues.filter(
        (venue: any) =>
          Array.isArray(venueUserConfig.venues) &&
          venueUserConfig.venues.indexOf(venue._id) > -1 &&
          venue.title
      );
    }

    return [];
  }, [allVenues, venueUserConfig]);

  useEffect(() => {
    if (!venues) {
      return;
    }
    setVenueData(
      venues?.map((venue: any) => {
        return {
          label: `${venue.title}`,
          value: venue._id
        };
      }) ?? []
    );
  }, [venues]);

  const filteredVenues = venueData.filter((venue) =>
    venue.label.toLowerCase().includes(searchCriteria.toLowerCase())
  );

  const getTabFromNavigationItem = (item: INavigationMenuItem): ToggleView => {
    const itemText = item.text.toLowerCase();
    if (itemText.includes("floorplan")) return "FloorplanTab";
    if (itemText.includes("run sheet") || itemText.includes("runsheet"))
      return "RunsheetTab";
    return "DiaryTab";
  };

  const handleValueChanged = (selectedVenueId: string) => {
    const targetTab = getTabFromNavigationItem(parentItem);

    if (
      parentItem.link?.startsWith("http://") ||
      parentItem.link?.startsWith("https://")
    ) {
      const venueLink = getVenueLink(parentItem, selectedVenueId);
      window.location.href = venueLink;
      return;
    }
    setExpandedItems?.(new Set()); // Collapse all items in the sidebar
    updateUrl({
      venueId: selectedVenueId,
      tab: targetTab,
      date: date
    });
  };

  if (!allVenues) {
    return (
      <div className="eds-venue-picker__loading flex items-center justify-center p-5 text-primary-color">
        <div className="eds-venue-picker__loading-text text-sm text-secondary-color">
          Loading venues...
        </div>
      </div>
    );
  }

  return (
    <div className="eds-venue-picker bg-panel-background border-t border-b border-border-color py-3 px-2 font-sans relative z-[1000]">
      <div className="eds-venue-picker__search mb-3">
        <EDSSearch
          placeholder="Search Venues"
          value={searchCriteria}
          onChange={(e) => setSearchCriteria(e.target.value)}
          className="eds-venue-picker__search-input w-full"
        />
      </div>
      <div className="eds-venue-picker__list overflow-y-auto max-h-[200px] rounded-md bg-panel-background">
        {filteredVenues.map((venue, index) => {
          const isSelected = currentVenueId === venue.value;
          const itemClasses = [
            "eds-venue-picker__item",
            "w-full py-3 px-4 bg-transparent border-none rounded-md",
            "text-primary-color text-sm font-normal text-left",
            "cursor-pointer transition-all duration-150 ease-in-out font-sans",
            "mb-0.5 last:mb-0",
            "hover:bg-[rgba(31,212,212,0.1)]",
            "focus:outline-2 focus:outline-accent-color focus:-outline-offset-2",
            isSelected ? "eds-venue-picker-item-selected" : ""
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={`venue-${index}`}
              className={itemClasses}
              onClick={() => handleValueChanged(venue.value)}
            >
              {venue.label}
            </button>
          );
        })}
        {filteredVenues.length === 0 && (
          <div className="eds-venue-picker__no-results p-5 text-center text-secondary-color text-sm italic">
            No venues found
          </div>
        )}
      </div>
    </div>
  );
};
